import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ComponentCard from '../../components/common/ComponentCard';
import SearchableSelect, {
  type SearchableSelectOption,
} from '../../components/common/SearchableSelect';
import Label from '../../components/form/Label';
import Input from '../../components/form/input/InputField';
import { Modal } from '../../components/ui/modal';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  getIntakeLegalName,
  getIntakePrimaryContact,
  getIntakeRestaurantName,
  getIntakeStoreId,
  getIntakeStoreName,
  type OnboardingIntake,
  type OnboardingIntakeFile,
} from '../../types/onboardingIntake';
import { api, apiEndpoints } from '../../utils/apiService';
import {
  ACCEPTED_FILE_COPY,
  ACCEPTED_FILE_TYPES,
  formatBytes,
  validateUploadFiles,
  validateUploadFilesBasic,
} from '../../utils/fileUploadValidation';
import {
  type TableSortState,
  toOrderingParam,
  toggleSortState,
} from '../../utils/listOrdering';
import NewIntakeLinkModal from './NewIntakeLinkModal';

type IntakeSortKey =
  | 'restaurant_name'
  | 'legal_name'
  | 'contact_name'
  | 'status'
  | 'store_name'
  | 'updated_at';

const INTAKE_SORT_KEY_TO_FIELD: Record<IntakeSortKey, string> = {
  restaurant_name: 'restaurant_name',
  legal_name: 'legal_name',
  contact_name: 'contact_name',
  status: 'status',
  store_name: 'store_name',
  updated_at: 'updated_at',
};

const INTAKE_COLUMNS: { key: IntakeSortKey; label: string }[] = [
  { key: 'restaurant_name', label: 'Restaurant' },
  { key: 'legal_name', label: 'Legal Business' },
  { key: 'contact_name', label: 'Primary Contact' },
  { key: 'status', label: 'Status' },
  { key: 'store_name', label: 'Linked Store' },
  { key: 'updated_at', label: 'Updated' },
];

interface OnboardingIntakesTabProps {
  storeOptions: SearchableSelectOption[];
  onOpenLinkedStore: (storeId: number) => void;
  onPopulateStoreCreate: (intake: OnboardingIntake) => void;
  onPopulateExistingStore: (intakeId: number, storeId: number) => void;
}

const statusOptions = [
  '',
  'new',
  'reviewing',
  'approved',
  'linked',
  'archived',
  // Shared intake links (group onboarding).
  'in_progress',
  'submitted',
];

const parseListResponse = (data: any): OnboardingIntake[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.intakes)) return data.intakes;
  return [];
};

/** What a shared link's row says in the Status column. */
export const linkStatus = (link: NonNullable<OnboardingIntake['link']>) => {
  switch (link.state) {
    case 'open':
      return `Link · ${link.confirmed} of ${link.total} confirmed`;
    case 'submitted':
      return 'Link · submitted, to approve';
    case 'approved':
      return 'Link · approved';
    case 'expired':
      return 'Link · expired';
    default:
      return 'Link · turned off';
  }
};

/**
 * Where an intake stands, for the colour-coded Status card: still being
 * filled in (Draft), form submitted but no store yet (Form completed), or
 * linked to a store (Store created). Archived rows say so instead.
 *
 * A direct intake only exists once the public form was submitted; a shared
 * link's intake exists from the moment the link is created, so an open,
 * expired or revoked link is still a draft.
 */
export type IntakeStage = 'draft' | 'completed' | 'store' | 'archived';

export const intakeStage = (intake: OnboardingIntake): IntakeStage => {
  if (intake.status === 'archived') return 'archived';
  if (getIntakeStoreId(intake) || intake.status === 'linked') return 'store';
  if (
    intake.link &&
    intake.link.state !== 'submitted' &&
    intake.link.state !== 'approved'
  )
    return 'draft';
  return 'completed';
};

const STAGE_BADGE: Record<IntakeStage, { label: string; className: string }> = {
  draft: {
    label: 'Draft',
    className:
      'bg-gray-100 text-gray-700 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700',
  },
  completed: {
    label: 'Form completed',
    className:
      'bg-brand-50 text-brand-700 ring-brand-200 dark:bg-brand-900/20 dark:text-brand-300 dark:ring-brand-800',
  },
  store: {
    label: 'Store created',
    className:
      'bg-success-50 text-success-700 ring-success-200 dark:bg-success-900/20 dark:text-success-400 dark:ring-success-800',
  },
  archived: {
    label: 'Archived',
    className:
      'bg-gray-100 text-gray-500 ring-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700',
  },
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function OnboardingIntakesTab({
  storeOptions,
  onOpenLinkedStore,
  onPopulateStoreCreate,
  onPopulateExistingStore,
}: OnboardingIntakesTabProps) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<TableSortState<IntakeSortKey>>({
    key: null,
    direction: 'desc',
  });
  const ordering = toOrderingParam(sort, INTAKE_SORT_KEY_TO_FIELD);
  const [status, setStatus] = useState('');
  const [storeId, setStoreId] = useState('');
  const [intakes, setIntakes] = useState<OnboardingIntake[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedIntake, setSelectedIntake] = useState<OnboardingIntake | null>(
    null,
  );
  const [editStatus, setEditStatus] = useState('');
  const [editPayloadText, setEditPayloadText] = useState('');
  const [saving, setSaving] = useState(false);
  const [adminUploadFiles, setAdminUploadFiles] = useState<File[]>([]);
  const [adminUploadError, setAdminUploadError] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<number | null>(
    null,
  );
  const [newLinkOpen, setNewLinkOpen] = useState(false);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const selectedStoreOptions = useMemo(
    () => [{ value: '', label: 'All stores' }, ...storeOptions],
    [storeOptions],
  );

  const fetchIntakes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(
        apiEndpoints.getOnboardingIntakes({
          search: search.trim(),
          status,
          store_id: storeId,
          ordering: ordering ?? undefined,
        }),
      );
      if (response?.ok) {
        setIntakes(parseListResponse(await response.json()));
      } else {
        setIntakes([]);
        setError('Failed to load onboarding intakes.');
      }
    } catch {
      setIntakes([]);
      setError('Failed to load onboarding intakes.');
    } finally {
      setLoading(false);
    }
  }, [ordering, search, status, storeId]);

  useEffect(() => {
    fetchIntakes();
  }, [fetchIntakes]);

  const openIntake = async (id: number) => {
    setError('');
    setSuccess('');
    try {
      const response = await api.get(apiEndpoints.getOnboardingIntake(id));
      if (response?.ok) {
        const intake = (await response.json()) as OnboardingIntake;
        setSelectedIntake(intake);
        setEditStatus(intake.status || 'new');
        setEditPayloadText(JSON.stringify(intake.payload || {}, null, 2));
      } else {
        setError('Failed to load intake details.');
      }
    } catch {
      setError('Failed to load intake details.');
    }
  };

  const closeModal = () => {
    setSelectedIntake(null);
    setEditPayloadText('');
    setEditStatus('');
    setAdminUploadFiles([]);
    setAdminUploadError('');
  };

  const downloadIntakeFile = async (
    intakeId: number,
    file: OnboardingIntakeFile,
  ) => {
    setDownloadingFileId(file.id);
    setError('');
    try {
      const response = await api.get(
        apiEndpoints.downloadOnboardingIntakeFile(intakeId, file.id),
      );
      if (response?.ok) {
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = file.original_filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
      } else {
        setError('Failed to download file.');
      }
    } catch {
      setError('Failed to download file.');
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handleAdminFileUpload = async () => {
    if (!selectedIntake || adminUploadFiles.length === 0) return;
    const validationError = await validateUploadFiles(adminUploadFiles);
    if (validationError) {
      setAdminUploadError(validationError);
      return;
    }

    setUploadingFiles(true);
    setError('');
    setSuccess('');
    try {
      const formData = new FormData();
      adminUploadFiles.forEach((file) => formData.append('files', file));
      const response = await api.postForm(
        apiEndpoints.uploadOnboardingIntakeFiles(selectedIntake.id),
        formData,
      );
      if (response?.ok) {
        const uploaded = (await response.json()) as OnboardingIntakeFile[];
        setSelectedIntake((prev) =>
          prev
            ? {
                ...prev,
                files: [...(prev.files || []), ...uploaded],
              }
            : prev,
        );
        setAdminUploadFiles([]);
        setAdminUploadError('');
        setSuccess(`Uploaded ${uploaded.length} file(s).`);
      } else {
        const data = await response?.json().catch(() => ({}));
        setError(data.detail || data.files || 'Failed to upload files.');
      }
    } catch {
      setError('Failed to upload files.');
    } finally {
      setUploadingFiles(false);
    }
  };

  const patchIntake = async (
    intake: OnboardingIntake,
    patch: Record<string, unknown>,
  ) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await api.patch(
        apiEndpoints.updateOnboardingIntake(intake.id),
        patch,
      );
      if (response?.ok) {
        const updated = (await response.json().catch(() => null)) || {
          ...intake,
          ...patch,
        };
        setSuccess('Intake updated.');
        setSelectedIntake(updated);
        setEditStatus(updated.status || editStatus);
        setIntakes((prev) =>
          prev.map((item) => (item.id === intake.id ? updated : item)),
        );
      } else {
        setError('Failed to update intake.');
      }
    } catch {
      setError('Failed to update intake.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!selectedIntake) return;
    let parsedPayload: unknown;
    try {
      parsedPayload = editPayloadText.trim() ? JSON.parse(editPayloadText) : {};
    } catch {
      setError('Payload JSON is invalid.');
      return;
    }
    await patchIntake(selectedIntake, {
      status: editStatus,
      payload: parsedPayload,
    });
  };

  const handleArchive = async (intake: OnboardingIntake) => {
    await patchIntake(intake, { status: 'archived' });
  };

  // Populating Store Create from a form that was never submitted copies a
  // half-filled payload; warn and let the admin decide.
  const [populateWarning, setPopulateWarning] =
    useState<OnboardingIntake | null>(null);

  const requestPopulateStoreCreate = (intake: OnboardingIntake) => {
    if (intakeStage(intake) === 'draft') {
      setPopulateWarning(intake);
      return;
    }
    onPopulateStoreCreate(intake);
  };

  const linkAction = async (
    intake: OnboardingIntake,
    action: 'revoke' | 'new-link',
  ) => {
    setError('');
    setSuccess('');
    const response = await api
      .post(
        action === 'revoke'
          ? apiEndpoints.revokeOnboardingLink(intake.id)
          : apiEndpoints.replaceOnboardingLink(intake.id),
      )
      .catch(() => null);
    if (!response?.ok) {
      setError('Couldn’t change the link.');
      return;
    }
    setSuccess(action === 'revoke' ? 'Link turned off.' : 'New link made.');
    fetchIntakes();
  };

  const copyLink = async (intake: OnboardingIntake) => {
    if (!intake.link?.url) return;
    await navigator.clipboard?.writeText(intake.link.url).catch(() => null);
    setCopiedId(intake.id);
  };

  return (
    <ComponentCard title="Onboarding Intakes" className="overflow-hidden">
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_220px_260px_auto]">
          <Input
            type="search"
            name="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search restaurant, contact, email..."
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="app-input rounded-2xl px-3 py-2 text-sm"
          >
            {statusOptions.map((option) => (
              <option key={option || 'all'} value={option}>
                {option ? option : 'All statuses'}
              </option>
            ))}
          </select>
          <SearchableSelect
            options={selectedStoreOptions}
            value={storeId}
            onChange={setStoreId}
            placeholder="All stores"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={fetchIntakes}
              className="rounded-2xl bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 disabled:opacity-60 dark:bg-gray-800 dark:text-gray-200"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            <button
              type="button"
              onClick={() => setNewLinkOpen(true)}
              className="rounded-2xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-700"
            >
              New intake link
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-error-200 bg-error-50 p-3 text-sm text-error-600 dark:border-error-800 dark:bg-error-900/20">
            {error}
          </div>
        )}
        {success && (
          <div className="rounded-lg border border-success-200 bg-success-50 p-3 text-sm text-success-600 dark:border-success-800 dark:bg-success-900/20">
            {success}
          </div>
        )}

        <div className="max-w-full overflow-x-auto rounded-2xl border border-gray-200 dark:border-white/10">
          <Table>
            <TableHeader className="border-b border-gray-100 bg-gray-50 dark:border-white/[0.05] dark:bg-white/[0.03]">
              <TableRow>
                {INTAKE_COLUMNS.map((column) => (
                  <TableCell
                    key={column.key}
                    isHeader
                    className="px-4 py-3 text-left text-theme-xs text-gray-500 dark:text-gray-400"
                  >
                    <button
                      type="button"
                      className="inline-flex items-center gap-1"
                      aria-label={`Sort by ${column.label}`}
                      onClick={() =>
                        setSort((current) =>
                          toggleSortState(current, column.key),
                        )
                      }
                    >
                      {column.label}
                      {sort.key === column.key
                        ? sort.direction === 'asc'
                          ? ' ↑'
                          : ' ↓'
                        : ''}
                    </button>
                  </TableCell>
                ))}
                <TableCell
                  isHeader
                  className="px-4 py-3 text-left text-theme-xs text-gray-500 dark:text-gray-400"
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
              {intakes.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="px-4 py-8 text-center text-sm text-gray-500"
                  >
                    {loading
                      ? 'Loading intakes...'
                      : 'No onboarding intakes found.'}
                  </TableCell>
                </TableRow>
              ) : (
                intakes.map((intake) => {
                  const contact = getIntakePrimaryContact(intake);
                  const linkedStoreId = getIntakeStoreId(intake);
                  const stage = STAGE_BADGE[intakeStage(intake)];
                  return (
                    <TableRow key={intake.id}>
                      <TableCell className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {getIntakeRestaurantName(intake) ||
                          intake.link?.location_name ||
                          '—'}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {getIntakeLegalName(intake) || '—'}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        <div>{contact.name || '—'}</div>
                        <div className="text-xs text-gray-500">
                          {[contact.email, contact.phone]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        <span
                          className={`inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${stage.className}`}
                        >
                          {stage.label}
                        </span>
                        <div className="mt-1 text-xs text-gray-500">
                          {intake.link
                            ? linkStatus(intake.link)
                            : intake.status || 'new'}
                        </div>
                        {intake.link && (
                          <div className="text-xs text-gray-500">
                            {intake.link.store_group_id ? (
                              <Link
                                to={`/onboarding?group=${intake.link.store_group_id}`}
                                className="underline hover:text-gray-700 dark:hover:text-gray-300"
                                title="Open this group's onboarding"
                              >
                                {intake.link.store_group_name || 'Group'}
                              </Link>
                            ) : (
                              'No group'
                            )}
                            {intake.link.copied_from_name
                              ? ` · from ${intake.link.copied_from_name}`
                              : ''}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {linkedStoreId
                          ? `${getIntakeStoreName(intake) || 'Store'} (ID: ${linkedStoreId})`
                          : '—'}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {formatDate(intake.updated_at || intake.created_at)}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-sm">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => openIntake(intake.id)}
                            className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200"
                          >
                            View/Edit
                          </button>
                          {linkedStoreId ? (
                            <button
                              type="button"
                              onClick={() =>
                                onPopulateExistingStore(
                                  intake.id,
                                  linkedStoreId,
                                )
                              }
                              className="rounded-lg bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-100 dark:bg-brand-900/20 dark:text-brand-300"
                            >
                              Apply to Linked Store
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => requestPopulateStoreCreate(intake)}
                              className="rounded-lg bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-100 dark:bg-brand-900/20 dark:text-brand-300"
                            >
                              {intake.link?.state === 'submitted'
                                ? 'Review and approve'
                                : 'Populate Store Create'}
                            </button>
                          )}
                          {intake.link?.state === 'open' && (
                            <>
                              <button
                                type="button"
                                onClick={() => copyLink(intake)}
                                className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200"
                              >
                                {copiedId === intake.id
                                  ? 'Copied'
                                  : 'Copy link'}
                              </button>
                              <button
                                type="button"
                                onClick={() => linkAction(intake, 'revoke')}
                                className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200"
                              >
                                Turn off link
                              </button>
                            </>
                          )}
                          {(intake.link?.state === 'expired' ||
                            intake.link?.state === 'revoked') && (
                            <button
                              type="button"
                              onClick={() => linkAction(intake, 'new-link')}
                              className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200"
                            >
                              New link
                            </button>
                          )}

                          {linkedStoreId && (
                            <button
                              type="button"
                              onClick={() => onOpenLinkedStore(linkedStoreId)}
                              className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200"
                            >
                              Open Store
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleArchive(intake)}
                            className="rounded-lg bg-error-50 px-3 py-1.5 text-xs font-medium text-error-600 hover:bg-error-100 dark:bg-error-900/20 dark:text-error-300"
                          >
                            Archive
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <NewIntakeLinkModal
        isOpen={newLinkOpen}
        onClose={() => setNewLinkOpen(false)}
        onCreated={fetchIntakes}
        storeOptions={storeOptions}
      />

      <Modal
        isOpen={Boolean(populateWarning)}
        onClose={() => setPopulateWarning(null)}
        className="max-w-md p-6"
      >
        {populateWarning && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              This form hasn’t been submitted
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {populateWarning.link
                ? `${getIntakeRestaurantName(populateWarning) || populateWarning.link.location_name || 'This restaurant'} is still filling in the onboarding form (${populateWarning.link.confirmed} of ${populateWarning.link.total} confirmed). `
                : 'The onboarding form hasn’t been submitted yet. '}
              Store Create will start from whatever is saved so far — missing
              answers come across blank.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPopulateWarning(null)}
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  onPopulateStoreCreate(populateWarning);
                  setPopulateWarning(null);
                }}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
              >
                Populate anyway
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={Boolean(selectedIntake)}
        onClose={closeModal}
        className="max-w-5xl p-6 lg:p-8"
      >
        {selectedIntake && (
          <div className="space-y-5">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Onboarding Intake #{selectedIntake.id}
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Status</Label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="app-input rounded-2xl px-3 py-2 text-sm"
                >
                  {statusOptions.filter(Boolean).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Linked Store</Label>
                <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:border-white/10 dark:bg-gray-800 dark:text-gray-200">
                  {getIntakeStoreId(selectedIntake)
                    ? `${getIntakeStoreName(selectedIntake) || 'Store'} (ID: ${getIntakeStoreId(selectedIntake)})`
                    : 'Not linked'}
                </div>
              </div>
            </div>
            <div>
              <Label>Uploaded files</Label>
              {selectedIntake.files?.length ? (
                <div className="mt-2 space-y-2 rounded-2xl border border-gray-200 bg-gray-50 p-3 dark:border-white/10 dark:bg-gray-900/40">
                  {selectedIntake.files.map((file) => (
                    <div
                      key={file.id}
                      className="flex flex-wrap items-center justify-between gap-3 text-sm"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium text-gray-900 dark:text-white">
                          {file.original_filename}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatBytes(file.size_bytes)} ·{' '}
                          {formatDate(file.uploaded_at)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          downloadIntakeFile(selectedIntake.id, file)
                        }
                        disabled={downloadingFileId === file.id}
                        className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-brand-600 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50 disabled:opacity-60 dark:bg-gray-800 dark:ring-gray-700"
                      >
                        {downloadingFileId === file.id
                          ? 'Downloading...'
                          : 'Download'}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-sm text-gray-500">No files uploaded.</p>
              )}
            </div>
            <div>
              <Label>Add files</Label>
              <p className="mb-2 text-sm text-gray-500">{ACCEPTED_FILE_COPY}</p>
              <input
                type="file"
                multiple
                accept={ACCEPTED_FILE_TYPES}
                disabled={uploadingFiles}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setAdminUploadFiles(files);
                  if (!files.length) {
                    setAdminUploadError('');
                    return;
                  }
                  setAdminUploadError(validateUploadFilesBasic(files));
                  void validateUploadFiles(files).then(setAdminUploadError);
                }}
                className="block w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs file:mr-4 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-gray-700 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:file:bg-gray-800 dark:file:text-gray-200"
              />
              {adminUploadError && (
                <p className="mt-2 text-sm text-error-600">
                  {adminUploadError}
                </p>
              )}
              {adminUploadFiles.length > 0 && (
                <button
                  type="button"
                  onClick={handleAdminFileUpload}
                  disabled={uploadingFiles || !!adminUploadError}
                  className="mt-3 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
                >
                  {uploadingFiles ? 'Uploading...' : 'Upload selected files'}
                </button>
              )}
            </div>
            <div>
              <Label>Payload JSON</Label>
              <textarea
                value={editPayloadText}
                onChange={(e) => setEditPayloadText(e.target.value)}
                rows={18}
                className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 font-mono text-xs text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
              />
            </div>
            <div className="flex flex-wrap justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-800">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleArchive(selectedIntake)}
                disabled={saving}
                className="rounded-lg bg-error-50 px-4 py-2 text-sm font-semibold text-error-600 hover:bg-error-100 disabled:opacity-60"
              >
                Archive
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
              >
                {saving ? 'Saving...' : 'Save Intake'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </ComponentCard>
  );
}
